import React, { useState, useRef } from 'react';
import { Company, PaymentRecord, BillData } from '../types/billing';
import { PaymentProcessor, BankTransaction } from '../services/paymentProcessor';

interface BankStatementUploaderProps {
  companies: Company[];
  bills: BillData[];
  onProcessPayments: (payments: PaymentRecord[]) => Promise<void>;
  onClose: () => void;
}

const BankStatementUploader: React.FC<BankStatementUploaderProps> = ({
  companies,
  bills,
  onProcessPayments,
  onClose
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [transactions, setTransactions] = useState<BankTransaction[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setError('');
    setSuccess('');
    setIsUploading(true);

    try {
      // Parse the bank statement file based on file type
      const fileExtension = selectedFile.name.split('.').pop()?.toLowerCase();
      let parsedTransactions: BankTransaction[] = [];

      if (fileExtension === 'pdf') {
        // PDF parsing is not fully implemented yet
        // Show a helpful error message with instructions
        setError('PDF upload is not currently supported. Please follow these steps:\n1. Log in to your bank\'s website\n2. Download your bank statement\n3. Export it as CSV format\n4. Upload the CSV file here\n\nCSV format provides more accurate and reliable parsing of transactions.');
        setIsUploading(false);
        return;
      } else if (fileExtension === 'csv') {
        parsedTransactions = await parseCSVStatement(selectedFile);
      } else {
        throw new Error('Unsupported file format. Please upload CSV format.');
      }

      setTransactions(parsedTransactions);
      setSuccess(`Successfully parsed ${parsedTransactions.length} transactions`);
    } catch (err: any) {
      setError(err.message || 'Failed to parse bank statement. Please check the file format.');
      console.error('Error parsing bank statement:', err);
    } finally {
      setIsUploading(false);
    }
  };

  // Parse CSV with proper handling of quoted fields and empty columns
  const parseCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  };

  const parseCSVStatement = async (file: File): Promise<BankTransaction[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const text = e.target?.result as string;
          const lines = text.split('\n').map(line => line.trim()).filter(line => line.length > 0);
          
          const transactions: BankTransaction[] = [];
          let headerFound = false;
          let dateIndex = -1;
          let narrationIndex = -1;
          let instrumentIndex = -1;
          let debitIndex = -1;
          let creditIndex = -1;
          let balanceIndex = -1;

          // Find the header row (contains "Date" and "Narration")
          let headerRowIndex = -1;
          for (let i = 0; i < lines.length; i++) {
            const columns = parseCSVLine(lines[i]);
            
            // Check if this is the header row
            const hasDate = columns.some(col => col.toLowerCase().includes('date') && col.toLowerCase() !== '');
            const hasNarration = columns.some(col => col.toLowerCase().includes('narration'));
            
            if (hasDate && hasNarration) {
              // Found header row, find column indices
              dateIndex = columns.findIndex(col => col.toLowerCase().includes('date') && col.toLowerCase() !== '');
              narrationIndex = columns.findIndex(col => col.toLowerCase().includes('narration'));
              instrumentIndex = columns.findIndex(col => col.toLowerCase().includes('instrument'));
              debitIndex = columns.findIndex(col => col.toLowerCase().includes('debit'));
              creditIndex = columns.findIndex(col => col.toLowerCase().includes('credit'));
              balanceIndex = columns.findIndex(col => col.toLowerCase().includes('balance'));
              
              headerFound = true;
              headerRowIndex = i;
              break; // Found header, exit loop
            }
          }

          // Process transaction rows after header is found
          if (headerFound && dateIndex >= 0 && narrationIndex >= 0) {
            for (let i = headerRowIndex + 1; i < lines.length; i++) {
              const columns = parseCSVLine(lines[i]);
              
              // Skip rows that don't have enough columns or are clearly not transactions
              if (columns.length <= Math.max(dateIndex, narrationIndex)) continue;
              
              const dateStr = columns[dateIndex]?.replace(/"/g, '').trim() || '';
              const narration = columns[narrationIndex]?.replace(/"/g, '').trim() || '';
              const instrument = instrumentIndex >= 0 ? columns[instrumentIndex]?.replace(/"/g, '').trim() || '' : '';
              
              // Skip rows that are clearly not transactions (empty date or narration)
              if (!dateStr || !narration || dateStr.toLowerCase() === 'date') continue;
              
              // Parse amounts and round to 2 decimal places (matching bill calculation precision)
              const debitStr = debitIndex >= 0 ? columns[debitIndex]?.replace(/"/g, '').replace(/,/g, '').trim() || '' : '';
              const creditStr = creditIndex >= 0 ? columns[creditIndex]?.replace(/"/g, '').replace(/,/g, '').trim() || '' : '';
              const balanceStr = balanceIndex >= 0 ? columns[balanceIndex]?.replace(/"/g, '').replace(/,/g, '').trim() || '' : '';
              
              // Round to 2 decimal places to match bill amount precision
              const roundAmount = (value: number): number => Math.round(value * 100) / 100;
              
              const debit = roundAmount(parseFloat(debitStr) || 0);
              const credit = roundAmount(parseFloat(creditStr) || 0);
              const balance = roundAmount(parseFloat(balanceStr) || 0);
              
              // Determine transaction type and amount
              let amount = 0;
              let type: 'credit' | 'debit' = 'debit';
              
              if (credit > 0) {
                amount = credit;
                type = 'credit';
              } else if (debit > 0) {
                amount = debit;
                type = 'debit';
              } else {
                continue; // Skip rows with no amount
              }

              // Format date from "18 Nov 2025" to ISO format
              const formattedDate = formatSaraswatDate(dateStr);
              
              if (formattedDate) {
                transactions.push({
                  date: formattedDate,
                  description: narration,
                  amount,
                  type,
                  balance,
                  reference: instrument || extractReference(narration)
                });
              }
            }
          }
          
          if (!headerFound) {
            reject(new Error('Could not find header row in CSV. Please ensure the file contains "Date" and "Narration" columns.'));
            return;
          }
          
          resolve(transactions);
        } catch (err) {
          reject(err);
        }
      };
      
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  };


  const formatSaraswatDate = (dateStr: string): string | null => {
    // Handle Saraswat Bank date format: "18 Nov 2025" (DD MMM YYYY)
    const months: { [key: string]: string } = {
      'jan': '01', 'feb': '02', 'mar': '03', 'apr': '04',
      'may': '05', 'jun': '06', 'jul': '07', 'aug': '08',
      'sep': '09', 'oct': '10', 'nov': '11', 'dec': '12'
    };

    // Try to parse "DD MMM YYYY" format
    const match = dateStr.match(/(\d{1,2})\s+([a-zA-Z]{3})\s+(\d{4})/i);
    if (match) {
      const day = match[1].padStart(2, '0');
      const month = months[match[2].toLowerCase().substring(0, 3)];
      const year = match[3];
      
      if (month) {
        return `${year}-${month}-${day}`;
      }
    }

    // Fallback: try standard Date parsing
    try {
      const date = new Date(dateStr);
      if (!isNaN(date.getTime())) {
        return date.toISOString().split('T')[0];
      }
    } catch (e) {
      // Ignore
    }

    return null;
  };

  const extractReference = (description: string): string => {
    // Extract reference numbers from description
    const refMatch = description.match(/(?:ref|refno|ref no|transaction|txn)[\s:]*([a-zA-Z0-9]+)/i);
    return refMatch ? refMatch[1] : '';
  };

  const processPayments = async () => {
    if (transactions.length === 0) {
      setError('No transactions to process');
      return;
    }

    setIsProcessing(true);
    setError('');

    try {
      // Use PaymentProcessor for intelligent matching
      const processor = new PaymentProcessor(companies, bills);
      
      // Count total credit transactions
      const creditTransactions = transactions.filter(t => t.type === 'credit');
      
      // Process transactions (only those matching company name or GST)
      const payments = processor.processTransactions(transactions);
      
      if (payments.length === 0) {
        const unmatchedCount = creditTransactions.length;
        const companyNames = companies.map(c => c.name).join(', ');
        setError(`No payments could be automatically matched. ${unmatchedCount} credit transaction(s) found, but none matched any company name in your system. Available companies: ${companyNames}. Please check the browser console (F12) for detailed matching information.`);
        console.log('=== BANK STATEMENT MATCHING DEBUG ===');
        console.log('Available companies:', companies.map(c => ({ name: c.name, id: c.id })));
        console.log('Credit transactions:', creditTransactions.map(t => ({ description: t.description, amount: t.amount })));
        console.log('=====================================');
        return;
      }

      // Get summary for display
      const summary = processor.getPaymentSummary(payments);
      
      const unmatchedCount = creditTransactions.length - payments.length;
      let successMessage = `Successfully processed ${payments.length} payment(s) (₹${summary.totalAmount.toLocaleString()})`;
      
      if (unmatchedCount > 0) {
        successMessage += `. ${unmatchedCount} transaction(s) were skipped as they didn't match any company name or GST number.`;
      }
      
      await onProcessPayments(payments);
      setSuccess(successMessage);
      
      // Auto-close after success
      setTimeout(() => {
        onClose();
      }, 3000);

    } catch (err) {
      setError('Failed to process payments. Please try again.');
      console.error('Error processing payments:', err);
    } finally {
      setIsProcessing(false);
    }
  };


  const resetUpload = () => {
    setFile(null);
    setTransactions([]);
    setError('');
    setSuccess('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-6 border w-full max-w-4xl shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold text-gray-900">Bank Statement Upload</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Instructions */}
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="font-semibold text-blue-900 mb-2">Instructions:</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Upload your bank statement in CSV format</li>
            <li>• Supports Saraswat Co-operative Bank CSV format</li>
            <li>• CSV should have columns: Date, Narration, Debit, Credit, Balance</li>
            <li>• Only credit transactions (money received) will be processed as payments</li>
            <li>• The system will automatically match transactions with existing companies</li>
            <li>• Date format should be: "DD MMM YYYY" (e.g., "18 Nov 2025")</li>
            <li>• Export your statement as CSV from your bank's website for best results</li>
          </ul>
        </div>

        {/* File Upload */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Upload Bank Statement (CSV)
          </label>
          <div className="flex items-center space-x-4">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-avis-blue file:text-white hover:file:bg-blue-700"
            />
            {file && (
              <button
                onClick={resetUpload}
                className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Status Messages */}
        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-md">
            {success}
          </div>
        )}

        {/* Loading States */}
        {isUploading && (
          <div className="mb-4 p-3 bg-blue-100 border border-blue-400 text-blue-700 rounded-md">
            Processing bank statement...
          </div>
        )}

        {/* Transactions Preview */}
        {transactions.length > 0 && (
          <div className="mb-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-3">
              Parsed Transactions ({transactions.length})
            </h4>
            <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Reference</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {transactions.slice(0, 10).map((transaction, index) => (
                    <tr key={index} className={transaction.type === 'credit' ? 'bg-green-50' : ''}>
                      <td className="px-4 py-2 text-sm text-gray-900">{transaction.date}</td>
                      <td className="px-4 py-2 text-sm text-gray-900 truncate max-w-xs">{transaction.description}</td>
                      <td className="px-4 py-2 text-sm text-gray-900">₹{transaction.amount.toLocaleString()}</td>
                      <td className="px-4 py-2 text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          transaction.type === 'credit' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {transaction.type}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-900">{transaction.reference}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {transactions.length > 10 && (
                <div className="px-4 py-2 text-sm text-gray-500 text-center">
                  ... and {transactions.length - 10} more transactions
                </div>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-avis-blue"
          >
            Cancel
          </button>
          
          {transactions.length > 0 && (
            <button
              onClick={processPayments}
              disabled={isProcessing}
              className="px-4 py-2 text-sm font-medium text-white bg-avis-blue border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-avis-blue disabled:opacity-50"
            >
              {isProcessing ? 'Processing...' : `Process ${transactions.filter(t => t.type === 'credit').length} Payments`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BankStatementUploader;
